import React, { useState, useEffect } from 'react';
import {
  Box, 
  TextField, 
  Button, 
  Typography, 
  FormControl, 
  FormLabel, 
  RadioGroup, 
  FormControlLabel, 
  Radio,
  FormGroup,
  Checkbox,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  InputLabel,
  OutlinedInput,
  Chip
} from '@mui/material';
import axios from 'axios';

// Reusable form component for adding/editing admin users
function AdminUserForm({ initialData, onSubmit, isEditing, availableApps }) {
  const [username, setUsername] = useState(initialData?.username || '');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [role, setRole] = useState(initialData?.role || 'regular_admin');
  const [selectedApps, setSelectedApps] = useState(initialData?.app_access || []);
  const [selectedOrgs, setSelectedOrgs] = useState(initialData?.organization_ids || []);
  const [availableOrgs, setAvailableOrgs] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // When initialData changes (e.g., loading complete in edit mode)
    if (initialData) {
      setUsername(initialData.username || '');
      setRole(initialData.role || 'regular_admin');
      // Map app_access from {id, name} to just [id]
      setSelectedApps((initialData.app_access || []).map(app => app.id));
      setSelectedOrgs(initialData.organization_ids || []);
      setPassword(''); // Always clear password fields
      setConfirmPassword('');
    }
  }, [initialData]);

  // Fetch organizations when role is org_admin
  useEffect(() => {
    if (role === 'org_admin' && selectedApps.length > 0) {
      const fetchOrgs = async () => {
        setLoadingOrgs(true);
        try {
          // Fetch orgs for the first selected app (org_admin usually has one app)
          const appId = selectedApps[0];
          const response = await axios.get(`/api/apps/${appId}/organizations`);
          setAvailableOrgs(response.data || []);
        } catch (err) {
          console.error('Failed to fetch organizations:', err);
          setAvailableOrgs([]);
        } finally {
          setLoadingOrgs(false);
        }
      };
      fetchOrgs();
    } else {
      setAvailableOrgs([]);
    }
  }, [role, selectedApps]);

  const handleAppSelectionChange = (event) => {
    const appId = parseInt(event.target.value, 10);
    if (event.target.checked) {
      setSelectedApps(prev => [...prev, appId]);
    } else {
      setSelectedApps(prev => prev.filter(id => id !== appId));
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (!isEditing && !password) {
      setError('Password is required for new users');
      return;
    }
    if ((role === 'regular_admin' || role === 'redaktion' || role === 'org_admin') && selectedApps.length === 0) {
        setError('Regular admins, Redaktion, and Org Admin users must have access to at least one app.');
        return;
    }
    if (role === 'org_admin' && selectedOrgs.length === 0) {
        setError('Org Admin users must be assigned to at least one organization.');
        return;
    }

    setSubmitting(true);
    const payload = {
      username,
      role,
      app_access: role !== 'full_admin' ? selectedApps : [],
    };
    if (role === 'org_admin') {
      payload.organization_ids = selectedOrgs;
    }

    if (password) {
      payload.password = password;
    }

    try {
      await onSubmit(payload);
      // Reset form or navigate away (handled by parent component)
    } catch (err) {
      setError(err.message || 'Failed to save admin user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      
      <TextField
        label="Username"
        fullWidth
        required
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        margin="normal"
        disabled={submitting}
      />
      
      <TextField
        label="Password"
        type="password"
        fullWidth
        required={!isEditing} // Only required when creating
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        margin="normal"
        placeholder={isEditing ? 'Leave blank to keep unchanged' : ''}
        disabled={submitting}
        helperText={isEditing ? 'Re-enter only to change the password.' : ''}
      />
      
      <TextField
        label="Confirm Password"
        type="password"
        fullWidth
        required={!!password} // Only required if password field is filled
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        margin="normal"
        disabled={submitting || !password}
      />
      
      <FormControl component="fieldset" margin="normal" fullWidth disabled={submitting}>
        <FormLabel component="legend">Role</FormLabel>
        <RadioGroup
          row
          aria-label="role"
          name="role"
          value={role}
          onChange={(e) => setRole(e.target.value)}
        >
          <FormControlLabel value="regular_admin" control={<Radio />} label="Regular Admin" />
          <FormControlLabel value="redaktion" control={<Radio />} label="Redaktion" />
          <FormControlLabel value="org_admin" control={<Radio />} label="Org Admin" />
          <FormControlLabel value="full_admin" control={<Radio />} label="Full Admin" />
        </RadioGroup>
      </FormControl>

      {role !== 'full_admin' && (
        <FormControl component="fieldset" margin="normal" fullWidth disabled={submitting}>
          <FormLabel component="legend">App Access</FormLabel>
          <FormGroup>
            {availableApps.length > 0 ? availableApps.map((app) => (
              <FormControlLabel
                key={app.id}
                control={
                  <Checkbox 
                    value={app.id} 
                    checked={selectedApps.includes(app.id)} 
                    onChange={handleAppSelectionChange} 
                  />
                }
                label={app.name}
              />
            )) : (
              <Typography variant="body2" color="text.secondary">No applications available.</Typography>
            )}
          </FormGroup>
        </FormControl>
      )}

      {role === 'org_admin' && (
        <FormControl component="fieldset" margin="normal" fullWidth disabled={submitting || loadingOrgs}>
          <FormLabel component="legend">Organization Access</FormLabel>
          {loadingOrgs ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <CircularProgress size={20} />
              <Typography variant="body2">Loading organizations...</Typography>
            </Box>
          ) : selectedApps.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Please select an app first to see available organizations.
            </Typography>
          ) : availableOrgs.length > 0 ? (
            <FormControl fullWidth sx={{ mt: 1 }}>
              <InputLabel id="org-select-label">Organizations</InputLabel>
              <Select
                labelId="org-select-label"
                multiple
                value={selectedOrgs}
                onChange={(e) => setSelectedOrgs(e.target.value)}
                input={<OutlinedInput label="Organizations" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((orgId) => {
                      const org = availableOrgs.find(o => o.id === orgId);
                      return <Chip key={orgId} label={org?.name || orgId} size="small" />;
                    })}
                  </Box>
                )}
              >
                {availableOrgs.map((org) => (
                  <MenuItem key={org.id} value={org.id}>
                    {org.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              No organizations found for the selected app.
            </Typography>
          )}
        </FormControl>
      )}

      <Button 
        type="submit" 
        variant="contained" 
        sx={{ mt: 3, mb: 2 }} 
        disabled={submitting}
      >
        {submitting ? <CircularProgress size={24} /> : (isEditing ? 'Update User' : 'Create User')}
      </Button>
    </Box>
  );
}

export default AdminUserForm; 